/*
  Theme wishlist page (sections/main-wishlist.liquid).

  Wishlist Hero owns the list and exposes it through window.WishListHero_SDK:
    GetWishListItems()           -> [{ ProductHandle, ProductVariantId, AddedOn, ... }]
                                    undefined until the app has loaded the list
    GetWishListHash(create)      -> hash string, or a Promise when it has to be created
    DeleteWishListItem({ ProductVariantId, WishlistHash }) -> Promise

  Every item is rendered with the theme's product card: the card HTML comes
  from the Section Rendering API on the product URL
  (/products/<handle>?variant=<id>&section_id=wishlist-card), so prices,
  badges and swatches match collection pages exactly.
*/
(function () {
  if (customElements.get('wishlist-page')) return;

  const SDK_TIMEOUT = 10000;
  const SDK_POLL = 150;
  const CONCURRENCY = 6;

  function getSdk() {
    return window.WishListHero_SDK;
  }

  function readItems() {
    const sdk = getSdk();
    if (!sdk || typeof sdk.GetWishListItems !== 'function') return undefined;
    try {
      return sdk.GetWishListItems();
    } catch (error) {
      return undefined;
    }
  }

  // The SDK object exists before the app has loaded the list, and the app
  // script itself loads after this one, so poll until an array comes back.
  function waitForItems() {
    return new Promise((resolve) => {
      const started = Date.now();
      (function poll() {
        const items = readItems();
        if (Array.isArray(items)) return resolve(items);
        if (Date.now() - started > SDK_TIMEOUT) return resolve(null);
        setTimeout(poll, SDK_POLL);
      })();
    });
  }

  // Newest first, the same order the app uses.
  function sortItems(items) {
    return items
      .filter((item) => item && item.ProductHandle && item.ProductVariantId)
      .sort((a, b) => new Date(b.AddedOn || 0) - new Date(a.AddedOn || 0));
  }

  function signature(items) {
    return items.map((item) => String(item.ProductVariantId)).join(',');
  }

  // Runs the async task for every entry with at most `limit` requests in flight.
  async function mapLimit(list, limit, task) {
    const results = new Array(list.length);
    let next = 0;
    async function worker() {
      while (next < list.length) {
        const index = next++;
        results[index] = await task(list[index], index);
      }
    }
    await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
    return results;
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.error = this.querySelector('[data-wishlist-error]');
      this.count = document.querySelector('[data-wishlist-count]');
      this.itemsByVariant = new Map();
      this.rendered = null;
      this.busy = false;

      this.grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-wishlist-remove]');
        if (button) this.remove(button);
      });

      // The list can change in another tab or on a product page reached with
      // the back button; re-read it when the page is shown again.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.refresh();
      });

      this.load();
    }

    async load() {
      const items = await waitForItems();
      if (items === null) {
        this.setState('error');
        return;
      }
      await this.render(items);
    }

    async refresh() {
      if (this.busy || this.rendered === null) return;
      const items = readItems();
      if (!Array.isArray(items)) return;
      if (signature(sortItems(items)) === this.rendered) return;
      await this.render(items);
    }

    async render(items) {
      this.busy = true;
      const list = sortItems(items);
      this.rendered = signature(list);
      this.itemsByVariant = new Map(list.map((item) => [String(item.ProductVariantId), item]));

      if (!list.length) {
        this.grid.replaceChildren();
        this.setState('empty');
        this.busy = false;
        return;
      }

      this.setState('loading');
      const cards = (await mapLimit(list, CONCURRENCY, (item) => this.fetchCard(item))).filter(Boolean);
      this.grid.replaceChildren(...cards);

      this.setState(cards.length ? 'list' : 'empty');
      this.busy = false;
    }

    async fetchCard(item) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url =
        root +
        'products/' +
        encodeURIComponent(item.ProductHandle) +
        '?variant=' +
        encodeURIComponent(item.ProductVariantId) +
        '&section_id=' +
        encodeURIComponent(this.dataset.cardSection);

      try {
        const response = await fetch(url);
        // A deleted or unpublished product answers 404: skip it. It is not
        // removed from the list, the product may come back.
        if (!response.ok) return null;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-wishlist-item]');
        if (!source) return null;

        const card = document.importNode(source, true);
        card.dataset.variantId = String(item.ProductVariantId);
        this.linkToVariant(card, item.ProductVariantId);
        return card;
      } catch (error) {
        return null;
      }
    }

    // Card links point at the product; open it on the saved colour and size.
    linkToVariant(card, variantId) {
      card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        if (url.searchParams.has('variant')) return;
        url.searchParams.set('variant', variantId);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });
    }

    async remove(button) {
      const card = button.closest('[data-wishlist-item]');
      const item = card && this.itemsByVariant.get(card.dataset.variantId);
      const sdk = getSdk();
      if (!item || !sdk) return;

      button.disabled = true;
      try {
        let hash = sdk.GetWishListHash(true);
        if (hash && typeof hash.then === 'function') hash = await hash;
        await sdk.DeleteWishListItem({ ProductVariantId: item.ProductVariantId, WishlistHash: hash });
      } catch (error) {
        // Checked below against the list itself.
      }

      const items = readItems();
      const stillThere =
        Array.isArray(items) && items.some((entry) => String(entry.ProductVariantId) === card.dataset.variantId);
      if (stillThere) {
        button.disabled = false;
        return;
      }

      this.itemsByVariant.delete(card.dataset.variantId);
      card.remove();
      this.rendered = signature(sortItems(Array.isArray(items) ? items : []));
      this.setState(this.grid.children.length ? 'list' : 'empty');
    }

    setState(state) {
      this.loading.hidden = state !== 'loading';
      this.grid.hidden = state !== 'list';
      this.empty.hidden = state !== 'empty';
      this.error.hidden = state !== 'error';
      this.updateCount(state === 'list' ? this.grid.children.length : 0);
    }

    updateCount(total) {
      if (!this.count) return;
      if (!total) {
        this.count.hidden = true;
        return;
      }
      const template = total === 1 ? this.dataset.countOne : this.dataset.countOther;
      this.count.textContent = (template || '[count]').replace('[count]', total);
      this.count.hidden = false;
    }
  }

  customElements.define('wishlist-page', WishlistPage);
})();
